import React, { useEffect, useMemo, useState } from "react";
import { Building2, MapPin, Edit, Trash2, Plus, Search, Refrigerator, Thermometer, Cpu, ChevronRight, Radio, Snowflake } from "lucide-react";

function Pill({ children }) {
  return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">{children}</span>;
}

function SearchField({ value, onChange, placeholder }) {
  return (
    <label className="relative block">
      <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:bg-white" />
    </label>
  );
}

function StatCard({ label, value, note }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
      <div className="text-xs text-slate-500">{note}</div>
    </div>
  );
}

async function parseJsonResponse(res, label) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    throw new Error(`Risposta non valida da ${label}`);
  }
}

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const norm = (value) => String(value || "").toLowerCase().trim();
const matchesQuery = (values, query) => {
  const needle = norm(query);
  if (!needle) return true;
  return values.some((value) => norm(value).includes(needle));
};

const FRIGO_LIMITS = {
  frigo: { label: "Frigorifero", limite_min: 0, limite_max: 10 },
  congelatore: { label: "Congelatore", limite_min: -30, limite_max: -12 },
};

function normalizeTipoFrigo(value, limiteMin, limiteMax) {
  const tipo = norm(value);
  if (tipo === "congelatore" || tipo === "freezer") return "congelatore";
  if (tipo === "frigo" || tipo === "frigorifero") return "frigo";
  const min = Number(limiteMin);
  const max = Number(limiteMax);
  if ((Number.isFinite(min) && min < 0) || (Number.isFinite(max) && max <= -12)) return "congelatore";
  return "frigo";
}

function formatLimit(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "n/d";
  return `${Number.isInteger(n) ? n : n.toFixed(1)}°C`;
}

function normalizeFrighiResponse(payload) {
  const items = Array.isArray(payload) ? payload : payload?.items;
  if (!Array.isArray(items)) return [];
  return items.map((item) => {
    const id = toNumber(item?.frigo_id ?? item?.id);
    if (!id) return null;
    const limiteMin = item?.limite_min ?? null;
    const limiteMax = item?.limite_max ?? null;
    return { id, frigo_id: id, nome: item?.nome || item?.name || `Frigo ${id}`, azienda_id: toNumber(item?.azienda_id), mac_address: item?.mac_address || item?.mac || "", temp: item?.temp ?? item?.temp_ultima ?? null, updated_at: item?.updated_at || item?.last_seen || null, limite_min: limiteMin, limite_max: limiteMax, tipo_conservazione: normalizeTipoFrigo(item?.tipo_conservazione ?? item?.tipo, limiteMin, limiteMax) };
  }).filter(Boolean);
}

function normalizeCryoResponse(payload) {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return items.map((item) => {
    const mac = item?.mac_address || item?.mac || "";
    if (!mac) return null;
    return { id: toNumber(item?.device_id ?? item?.id), device_id: toNumber(item?.device_id ?? item?.id), nome: item?.nome || item?.label || item?.name || null, mac_address: mac, azienda_id: toNumber(item?.azienda_id), frigo_id: toNumber(item?.frigo_id), frigo_nome: item?.frigo_nome || item?.name || null, source: item?.source || "cryo", assegnato: item?.assegnato === true || Number(item?.frigo_id || 0) > 0 || item?.status === "assegnato" };
  }).filter(Boolean);
}

function normalizeLorawanResponse(payload) {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return items.map((item) => {
    const mac = item?.mac_address || item?.mac || "";
    if (!mac) return null;
    return { id: toNumber(item?.device_id ?? item?.id), device_id: toNumber(item?.device_id ?? item?.id), nome: item?.nome || item?.device_model || item?.external_device_id || "Dispositivo LoRaWAN", mac_address: mac, external_device_id: item?.external_device_id || null, azienda_id: toNumber(item?.azienda_id), frigo_id: toNumber(item?.frigo_id), frigo_nome: item?.frigo_nome || null, assegnato: item?.assegnato === true || Number(item?.frigo_id || 0) > 0 };
  }).filter(Boolean);
}

function deviceVisibleForFrigo(device, frigo) {
  const frigoAziendaId = Number(frigo?.azienda_id || 0);
  const deviceAziendaId = Number(device?.azienda_id || 0);
  const assignedToSameFrigo = Number(device?.frigo_id || 0) === Number(frigo?.id || 0);
  if (assignedToSameFrigo) return true;
  if (!deviceAziendaId) return true;
  return deviceAziendaId === frigoAziendaId;
}

export default function AziendeFrighi({ isSuper }) {
  const [me, setMe] = useState(null);
  const [aziende, setAziende] = useState([]);
  const [frighi, setFrighi] = useState([]);
  const [sonde, setSonde] = useState([]);
  const [lorawanDevices, setLorawanDevices] = useState([]);
  const [selectedAziendaId, setSelectedAziendaId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState("");
  const [aziendaQuery, setAziendaQuery] = useState("");
  const [frigoQuery, setFrigoQuery] = useState("");
  const [cryoQuery, setCryoQuery] = useState("");
  const [lorawanQuery, setLorawanQuery] = useState("");

  const loadData = async () => {
    setLoading(true);
    setErrore("");
    try {
      const requests = [
        fetch("/api/me.php", { credentials: "include" }),
        fetch("/api/api_cryo_list.php?limit=500", { credentials: "include" }),
        fetch("/api/admin/sonde_list.php?limit=500", { credentials: "include" }),
        fetch("/api/admin/lorawan_devices_list.php?limit=500", { credentials: "include" }),
      ];
      if (isSuper) requests.unshift(fetch("/api/admin/aziende_list.php", { credentials: "include" }));
      const responses = await Promise.all(requests);
      let aziendePayload = [];
      let mePayload;
      let frighiPayload;
      let sondePayload;
      let lorawanPayload;
      if (isSuper) {
        const [azRes, meRes, frRes, sRes, lRes] = responses;
        aziendePayload = await parseJsonResponse(azRes, "aziende_list.php");
        mePayload = await parseJsonResponse(meRes, "me.php");
        frighiPayload = await parseJsonResponse(frRes, "api_cryo_list.php");
        sondePayload = await parseJsonResponse(sRes, "sonde_list.php");
        lorawanPayload = await parseJsonResponse(lRes, "lorawan_devices_list.php");
      } else {
        const [meRes, frRes, sRes, lRes] = responses;
        mePayload = await parseJsonResponse(meRes, "me.php");
        frighiPayload = await parseJsonResponse(frRes, "api_cryo_list.php");
        sondePayload = await parseJsonResponse(sRes, "sonde_list.php");
        lorawanPayload = await parseJsonResponse(lRes, "lorawan_devices_list.php");
      }

      const meData = mePayload || null;
      const azIdSession = toNumber(meData?.azienda_id);
      let aziendeList = Array.isArray(aziendePayload) ? aziendePayload : [];
      let frighiList = normalizeFrighiResponse(frighiPayload);
      let sondeList = normalizeCryoResponse(sondePayload);
      let lorawanList = normalizeLorawanResponse(lorawanPayload);

      if (!isSuper && azIdSession) {
        frighiList = frighiList.filter((item) => Number(item.azienda_id) === Number(azIdSession));
        sondeList = sondeList.filter((item) => !Number(item.azienda_id || 0) || Number(item.azienda_id) === Number(azIdSession));
        lorawanList = lorawanList.filter((item) => !Number(item.azienda_id || 0) || Number(item.azienda_id) === Number(azIdSession));
        aziendeList = [{ id: azIdSession, nome: meData?.azienda_nome || "La mia azienda", indirizzo: "" }];
      }

      setMe(meData);
      setAziende(aziendeList);
      setFrighi(frighiList);
      setSonde(sondeList);
      setLorawanDevices(lorawanList);
      const availableIds = aziendeList.map((item) => Number(item.id)).filter(Boolean);
      setSelectedAziendaId(isSuper ? ((prev) => prev) : azIdSession || null);
      if (isSuper) {
        setSelectedAziendaId((prev) => (prev && availableIds.includes(Number(prev)) ? Number(prev) : availableIds[0] || null));
      }
    } catch (err) {
      console.error(err);
      setErrore("Errore nel caricamento: " + (err.message || "ERRORE"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedAzienda = useMemo(() => (selectedAziendaId ? aziende.find((item) => Number(item.id) === Number(selectedAziendaId)) || null : null), [aziende, selectedAziendaId]);
  const aziendeFiltrate = useMemo(() => aziende.filter((item) => matchesQuery([item.nome, item.indirizzo, item.id], aziendaQuery)), [aziende, aziendaQuery]);
  const frighiPerAzienda = useMemo(() => {
    if (!selectedAziendaId) return [];
    return frighi.filter((item) => Number(item.azienda_id) === Number(selectedAziendaId) && matchesQuery([item.nome, item.mac_address, item.id, item.temp, item.tipo_conservazione], frigoQuery));
  }, [frighi, selectedAziendaId, frigoQuery]);
  const sondePerAzienda = useMemo(() => (!selectedAziendaId ? [] : sonde.filter((item) => !Number(item.azienda_id || 0) || Number(item.azienda_id) === Number(selectedAziendaId))), [sonde, selectedAziendaId]);
  const lorawanPerAzienda = useMemo(() => (!selectedAziendaId ? [] : lorawanDevices.filter((item) => !Number(item.azienda_id || 0) || Number(item.azienda_id) === Number(selectedAziendaId))), [lorawanDevices, selectedAziendaId]);
  const cryoByFrigoId = useMemo(() => new Map(sonde.filter((item) => item.frigo_id).map((item) => [Number(item.frigo_id), item])), [sonde]);
  const lorawanByFrigoId = useMemo(() => new Map(lorawanDevices.filter((item) => item.frigo_id).map((item) => [Number(item.frigo_id), item])), [lorawanDevices]);

  const sondeDisponibiliPerFrigo = (frigo) => sonde.filter((item) => deviceVisibleForFrigo(item, frigo)).filter((item) => matchesQuery([item.mac_address, item.nome, item.frigo_nome, item.device_id], cryoQuery)).sort((a, b) => {
    const aCurrent = Number(a.frigo_id || 0) === Number(frigo.id);
    const bCurrent = Number(b.frigo_id || 0) === Number(frigo.id);
    if (aCurrent === bCurrent) return String(a.mac_address).localeCompare(String(b.mac_address));
    return aCurrent ? -1 : 1;
  });

  const lorawanDisponibiliPerFrigo = (frigo) => lorawanDevices.filter((item) => deviceVisibleForFrigo(item, frigo)).filter((item) => matchesQuery([item.mac_address, item.nome, item.external_device_id, item.frigo_nome, item.device_id], lorawanQuery)).sort((a, b) => {
    const aCurrent = Number(a.frigo_id || 0) === Number(frigo.id);
    const bCurrent = Number(b.frigo_id || 0) === Number(frigo.id);
    if (aCurrent === bCurrent) return String(a.mac_address).localeCompare(String(b.mac_address));
    return aCurrent ? -1 : 1;
  });

  const creaNuovaAzienda = async () => {
    const nome = window.prompt("Nome azienda");
    if (!nome) return;
    const indirizzo = window.prompt("Indirizzo (opzionale)") || "";
    try {
      const res = await fetch("/api/admin/azienda_save.php", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: 0, nome, indirizzo }) });
      const data = await parseJsonResponse(res, "azienda_save.php");
      if (!data?.ok) return alert(data?.msg || data?.detail || data?.error || "Errore creazione azienda");
      await loadData();
    } catch (err) {
      alert("Errore creazione azienda: " + err.message);
    }
  };

  const rinominaAzienda = async (az) => {
    const nome = window.prompt("Nuovo nome azienda", az.nome);
    if (!nome || nome === az.nome) return;
    const indirizzo = window.prompt("Indirizzo", az.indirizzo || "") || "";
    try {
      const res = await fetch("/api/admin/azienda_save.php", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...az, id: az.id, nome, indirizzo }) });
      const data = await parseJsonResponse(res, "azienda_save.php");
      if (!data?.ok) return alert(data?.msg || data?.detail || data?.error || "Errore salvataggio azienda");
      await loadData();
    } catch (err) {
      alert("Errore salvataggio azienda: " + err.message);
    }
  };

  const configuraPartnerAcademy = async (az) => {
    const academy_partner_nome = window.prompt("Nome del rivenditore / ente che eroga il servizio", az.academy_partner_nome || "");
    if (academy_partner_nome === null) return;
    const academy_partner_logo_url = window.prompt("URL del logo (https://... oppure /percorso)", az.academy_partner_logo_url || "");
    if (academy_partner_logo_url === null) return;
    const academy_partner_email = window.prompt("Email del rivenditore", az.academy_partner_email || "");
    if (academy_partner_email === null) return;
    const academy_partner_phone = window.prompt("Telefono del rivenditore", az.academy_partner_phone || "");
    if (academy_partner_phone === null) return;
    const academy_partner_address = window.prompt("Indirizzo della sede del rivenditore", az.academy_partner_address || "");
    if (academy_partner_address === null) return;
    try {
      const res = await fetch("/api/admin/azienda_save.php", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...az, academy_partner_nome, academy_partner_logo_url, academy_partner_email, academy_partner_phone, academy_partner_address }) });
      const data = await parseJsonResponse(res, "azienda_save.php");
      if (!data?.ok) return alert(data?.error === "INVALID_LOGO_URL" ? "Inserisci un URL https:// oppure un percorso che inizi con /." : data?.error || "Errore salvataggio partner Academy");
      await loadData();
    } catch (err) {
      alert("Errore salvataggio partner Academy: " + err.message);
    }
  };

  const eliminaAzienda = async (az) => {
    if (!window.confirm(`Vuoi davvero eliminare l'azienda "${az.nome}"? Verranno rimossi anche i frighi collegati.`)) return;
    try {
      const res = await fetch("/api/admin/azienda_delete.php", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: az.id }) });
      const data = await parseJsonResponse(res, "azienda_delete.php");
      if (!data?.ok) return alert(data?.msg || data?.detail || data?.error || "Errore eliminazione azienda");
      await loadData();
    } catch (err) {
      alert("Errore eliminazione azienda: " + err.message);
    }
  };

  const creaFrigoPerAzienda = async () => {
    if (!selectedAziendaId) return;
    const nome = window.prompt("Nome nuovo frigo", "Frigo banco 1");
    if (!nome) return;
    const tipo = window.confirm("Vuoi configurarlo come congelatore? Premi Annulla per frigorifero.") ? "congelatore" : "frigo";
    try {
      const res = await fetch("/api/admin/frigo_create.php", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ nome, azienda_id: Number(selectedAziendaId), tipo_conservazione: tipo }) });
      const data = await parseJsonResponse(res, "frigo_create.php");
      if (!data?.ok) return alert(data?.msg || data?.detail || data?.error || "Errore creazione frigo");
      await loadData();
    } catch (err) {
      alert("Errore creazione frigo: " + err.message);
    }
  };

  const aggiornaTipoFrigo = async (frigo, tipo) => {
    if (!frigo?.id || !tipo || tipo === frigo.tipo_conservazione) return;
    try {
      const res = await fetch("/api/admin/frigo_rename.php", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: frigo.id, nome: frigo.nome || `Frigo ${frigo.id}`, tipo_conservazione: tipo }) });
      const data = await parseJsonResponse(res, "frigo_rename.php");
      if (!data?.ok) return alert(data?.msg || data?.detail || data?.error || "Errore aggiornamento tipologia frigo");
      await loadData();
    } catch (err) {
      alert("Errore aggiornamento tipologia frigo: " + err.message);
    }
  };

  const rinominaFrigo = async (frigo) => {
    const nuovo = window.prompt("Nuovo nome frigo", frigo.nome || `Frigo ${frigo.id}`);
    if (!nuovo || nuovo === frigo.nome) return;
    try {
      const res = await fetch("/api/admin/frigo_rename.php", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: frigo.id, nome: nuovo }) });
      const data = await parseJsonResponse(res, "frigo_rename.php");
      if (!data?.ok) return alert(data?.msg || data?.detail || data?.error || "Errore rinomina frigo");
      await loadData();
    } catch (err) {
      alert("Errore rinomina frigo: " + err.message);
    }
  };

  const eliminaFrigo = async (frigo) => {
    if (!window.confirm(`Eliminare il frigo "${frigo.nome || frigo.id}"? Il dispositivo collegato tornerà libero.`)) return;
    try {
      const res = await fetch("/api/admin/frigo_delete.php", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id: frigo.id }) });
      const data = await parseJsonResponse(res, "frigo_delete.php");
      if (!data?.ok) return alert(data?.msg || data?.detail || data?.error || "Errore eliminazione frigo");
      await loadData();
    } catch (err) {
      alert("Errore eliminazione frigo: " + err.message);
    }
  };

  const assegnaSondaAFrigo = async (device, frigoId) => {
    if (!device?.mac_address || !frigoId) return;
    try {
      const body = new URLSearchParams({ frigo_id: String(frigoId), device_id: String(device.device_id || ""), mac_address: device.mac_address });
      const res = await fetch("/api/admin/sonda_assign.php", { method: "POST", credentials: "include", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body });
      const data = await parseJsonResponse(res, "sonda_assign.php");
      if (!data?.ok) return alert(data?.message || data?.detail || data?.error || "Errore assegnazione sonda");
      await loadData();
    } catch (err) {
      alert("Errore assegnazione sonda: " + err.message);
    }
  };

  const assegnaLorawanAFrigo = async (device, frigoId, force = false) => {
    if (!device?.mac_address || !frigoId) return;
    try {
      const res = await fetch("/api/admin/lorawan_assign.php", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ frigo_id: Number(frigoId), device_id: device.device_id, mac_address: device.mac_address, force: force ? 1 : 0 }) });
      const data = await parseJsonResponse(res, "lorawan_assign.php");
      if (data?.ok) return void (await loadData());
      if (data?.error === "DEVICE_ALREADY_ASSIGNED" && !force) {
        if (window.confirm("Questo dispositivo LoRaWAN è già assegnato a un altro frigo. Vuoi forzare lo spostamento?")) await assegnaLorawanAFrigo(device, frigoId, true);
        return;
      }
      alert(data?.message || data?.detail || data?.error || "Errore assegnazione LoRaWAN");
    } catch (err) {
      alert("Errore assegnazione LoRaWAN: " + err.message);
    }
  };

  if (loading) return <div>Caricamento aziende e frighi...</div>;

  return (
    <div className="space-y-4">
      {errore ? <div className="bg-amber-100 text-amber-900 px-3 py-2 rounded text-sm">{errore}</div> : null}

      <div className="grid gap-3 md:grid-cols-4">
        <StatCard label="Aziende" value={aziende.length} note="Totale aziende visibili" />
        <StatCard label="Frighi" value={selectedAzienda ? frighiPerAzienda.length : frighi.length} note="Nel contesto selezionato" />
        <StatCard label="Cryo" value={selectedAzienda ? sondePerAzienda.length : sonde.length} note="Sonde disponibili" />
        <StatCard label="LoRaWAN" value={selectedAzienda ? lorawanPerAzienda.length : lorawanDevices.length} note="Dispositivi disponibili" />
      </div>

      <div className={"grid gap-4 " + (isSuper ? "grid-cols-1 lg:grid-cols-[minmax(0,280px),minmax(0,1fr)]" : "grid-cols-1")}>
        {isSuper ? (
          <div className="bg-white rounded-2xl border shadow-sm p-3 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-sm font-semibold flex items-center gap-2"><Building2 size={16} /> Aziende</h2>
                <p className="text-xs text-slate-500">Seleziona un&apos;azienda per vedere i relativi frighi.</p>
              </div>
              <button onClick={creaNuovaAzienda} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-2.5 py-1 text-xs flex items-center gap-1"><Plus size={12} /> Nuova</button>
            </div>
            <SearchField value={aziendaQuery} onChange={setAziendaQuery} placeholder="Cerca azienda, indirizzo o ID..." />
            <div className="space-y-2 max-h-[480px] overflow-auto pr-1">
              {aziendeFiltrate.map((az) => {
                const selected = Number(az.id) === Number(selectedAziendaId);
                return (
                  <button key={az.id} onClick={() => setSelectedAziendaId(Number(az.id))} className={"w-full text-left rounded-xl border px-3 py-3 flex items-start justify-between gap-2 transition " + (selected ? "bg-slate-900 border-slate-900 text-white shadow-sm" : "bg-slate-50 border-slate-200 hover:bg-slate-100")}>
                    <div className="min-w-0">
                      <div className="text-xs font-semibold flex items-center gap-1"><Building2 size={13} /> {az.nome}</div>
                      <div className="text-[11px] opacity-80 flex items-center gap-1 mt-1 truncate"><MapPin size={11} />{az.indirizzo || "Indirizzo non inserito"}</div>
                      <div className="text-[11px] opacity-70 mt-1">ID: {az.id}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <ChevronRight size={14} className="opacity-70" />
                      <div className="flex gap-1 mt-1">
                        <button type="button" onClick={(e) => { e.stopPropagation(); configuraPartnerAcademy(az); }} className="text-[10px] px-1.5 py-0.5 rounded bg-white/20 border border-white/30">Academy</button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); rinominaAzienda(az); }} className="text-[10px] px-1.5 py-0.5 rounded bg-white/20 border border-white/30">Modifica</button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); eliminaAzienda(az); }} className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200">Del</button>
                      </div>
                    </div>
                  </button>
                );
              })}
              {!aziendeFiltrate.length ? <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-xs text-slate-500">Nessuna azienda trovata con il filtro corrente.</div> : null}
            </div>
          </div>
        ) : null}

        <div className="space-y-3">
          {selectedAzienda ? (
            <div className="bg-gradient-to-r from-slate-900 via-indigo-700 to-sky-500 text-white rounded-2xl p-5 flex flex-wrap gap-4 items-center justify-between shadow-sm">
              <div className="flex gap-3 items-center">
                <div className="h-12 w-12 rounded-2xl bg-white/15 flex items-center justify-center"><Building2 size={22} /></div>
                <div>
                  <div className="text-xs uppercase tracking-wide opacity-80">Profilo azienda</div>
                  <div className="text-lg font-semibold">{selectedAzienda.nome}</div>
                  <div className="text-xs opacity-90 flex items-center gap-1"><MapPin size={12} />{selectedAzienda.indirizzo || "Indirizzo non inserito"}</div>
                  <div className="text-[11px] opacity-80 mt-1">ID azienda: {selectedAzienda.id}</div>
                  {me?.is_super ? <div className="text-[11px] opacity-80 mt-1">Modalità superadmin attiva</div> : null}
                </div>
              </div>
              <div className="text-right space-y-1">
                <div className="flex justify-end gap-1 flex-wrap"><Pill><Refrigerator size={11} className="mr-1" />{frighiPerAzienda.length} frighi</Pill></div>
                <div className="flex justify-end gap-1 flex-wrap">
                  <Pill><Cpu size={11} className="mr-1" />{sondePerAzienda.length} Cryo</Pill>
                  <Pill><Radio size={11} className="mr-1" />{lorawanPerAzienda.length} LoRaWAN</Pill>
                </div>
                <button onClick={creaFrigoPerAzienda} className="mt-2 inline-flex items-center gap-1 rounded-full bg-white text-slate-900 text-xs px-3 py-1.5 hover:bg-slate-100"><Plus size={12} /> Nuovo frigo per questa azienda</button>
              </div>
            </div>
          ) : <div className="bg-white border rounded-2xl p-4 text-sm text-slate-500">Seleziona un&apos;azienda per iniziare a gestire frighi e dispositivi.</div>}

          <div className="bg-white rounded-2xl border shadow-sm p-4">
            <div className="flex flex-col gap-3 mb-3 xl:flex-row xl:items-end xl:justify-between">
              <div>
                <h2 className="text-sm font-semibold flex items-center gap-2"><Refrigerator size={16} />{isSuper ? "Frighi dell'azienda selezionata" : "I miei frighi"}</h2>
                <p className="text-xs text-slate-500">Da qui puoi assegnare o riassegnare dispositivi Cryo e LoRaWAN.</p>
              </div>
              <div className="grid gap-2 md:grid-cols-3 xl:min-w-[760px]">
                <SearchField value={frigoQuery} onChange={setFrigoQuery} placeholder="Cerca frigo o MAC..." />
                <SearchField value={cryoQuery} onChange={setCryoQuery} placeholder="Filtra sonde Cryo..." />
                <SearchField value={lorawanQuery} onChange={setLorawanQuery} placeholder="Filtra LoRaWAN..." />
              </div>
            </div>

            <div className={"grid gap-3 mt-2 " + (isSuper ? "md:grid-cols-2 xl:grid-cols-3" : "sm:grid-cols-2 xl:grid-cols-4")}>
              {frighiPerAzienda.map((frigo) => {
                const tempLabel = frigo.temp !== undefined && frigo.temp !== null ? `${Number(frigo.temp).toFixed(1)}°C` : "n/d";
                const currentCryo = cryoByFrigoId.get(Number(frigo.id)) || null;
                const currentLorawan = lorawanByFrigoId.get(Number(frigo.id)) || null;
                const linkedType = currentLorawan ? "LoRaWAN" : currentCryo ? "Cryo" : frigo.mac_address ? "Dispositivo collegato" : "Nessun dispositivo";
                const sondeDisponibili = sondeDisponibiliPerFrigo(frigo);
                const lorawanDisponibili = lorawanDisponibiliPerFrigo(frigo);
                const tipo = normalizeTipoFrigo(frigo.tipo_conservazione, frigo.limite_min, frigo.limite_max);
                const limits = FRIGO_LIMITS[tipo];

                return (
                  <div key={frigo.id} className="rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-4 flex flex-col gap-3 shadow-sm">
                    <div className="flex gap-3 min-w-0">
                      <div className="h-10 w-10 shrink-0 rounded-2xl bg-slate-100 flex items-center justify-center border border-slate-200"><Refrigerator size={18} className="text-slate-600" /></div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-semibold text-sm text-slate-900">{frigo.nome || `Frigo ${frigo.id}`}</div>
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">{linkedType}</span>
                        </div>
                        <div className="mt-2 grid gap-1 text-[11px] text-slate-500">
                          <div>ID frigo: {frigo.id}</div>
                          <div>MAC collegato: {frigo.mac_address || "—"}</div>
                          <div>Azienda: {frigo.azienda_id || "—"}</div>
                          <div className="flex items-center gap-1"><Thermometer size={12} /> Temp: {tempLabel}</div>
                          <div className="flex items-center gap-1">{tipo === "congelatore" ? <Snowflake size={12} /> : <Refrigerator size={12} />} {limits.label}: {formatLimit(frigo.limite_min)} / {formatLimit(frigo.limite_max)}</div>
                        </div>
                      </div>
                    </div>

                    <label className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-3">
                      <div className="text-[11px] font-semibold text-emerald-900 mb-1">Tipologia e limiti automatici</div>
                      <select className="w-full text-xs border border-emerald-200 rounded-xl px-2 py-2 bg-white" value={tipo} onChange={(e) => aggiornaTipoFrigo(frigo, e.target.value)}>
                        <option value="frigo">Frigorifero (0 / 10 °C)</option>
                        <option value="congelatore">Congelatore (-30 / -12 °C)</option>
                      </select>
                    </label>

                    <div className="grid gap-2 sm:grid-cols-3">
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wide text-slate-400">Cryo attuale</div>
                        <div className="mt-1 text-xs font-medium text-slate-700 break-all">{currentCryo?.mac_address || "Nessuna sonda"}</div>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wide text-slate-400">LoRaWAN attuale</div>
                        <div className="mt-1 text-xs font-medium text-slate-700 break-all">{currentLorawan?.mac_address || "Nessun device"}</div>
                      </div>
                      <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                        <div className="text-[10px] uppercase tracking-wide text-slate-400">Filtri attivi</div>
                        <div className="mt-1 text-xs font-medium text-slate-700">{[frigoQuery, cryoQuery, lorawanQuery].filter(Boolean).length || 0}</div>
                      </div>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => rinominaFrigo(frigo)} className="text-[11px] px-2.5 py-1.5 rounded-xl bg-white border border-slate-200 hover:bg-slate-100 flex items-center gap-1"><Edit size={11} /> Rinomina</button>
                      <button onClick={() => eliminaFrigo(frigo)} className="text-[11px] px-2.5 py-1.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100 flex items-center gap-1"><Trash2 size={11} /> Elimina</button>
                    </div>

                    <div className="rounded-2xl border border-sky-100 bg-sky-50/70 p-3">
                      <div className="text-[11px] font-semibold text-sky-900 mb-1">Assegna / cambia sonda Cryo</div>
                      <div className="text-[10px] text-sky-800/80 mb-2">{cryoQuery ? `Filtro attivo: "${cryoQuery}"` : "Puoi cercare per MAC, nome sonda o frigo collegato."}</div>
                      <select className="w-full text-xs border border-sky-200 rounded-xl px-2 py-2 bg-white" defaultValue="" onChange={(e) => { const selected = sondeDisponibili.find((item) => item.mac_address === e.target.value); if (selected) assegnaSondaAFrigo(selected, frigo.id); }}>
                        <option value="">{currentCryo ? `Attuale: ${currentCryo.mac_address}` : "Seleziona una sonda Cryo..."}</option>
                        {sondeDisponibili.map((item) => <option key={item.mac_address} value={item.mac_address}>{item.mac_address}{item.nome ? ` - ${item.nome}` : ""}{Number(item.frigo_id || 0) === Number(frigo.id) ? " (attuale)" : ""}</option>)}
                      </select>
                      {!sondeDisponibili.length ? <div className="mt-2 text-[11px] text-sky-800">Nessuna sonda Cryo trovata con il filtro corrente.</div> : null}
                    </div>

                    <div className="rounded-2xl border border-violet-100 bg-violet-50/70 p-3">
                      <div className="text-[11px] font-semibold text-violet-900 mb-1">Assegna / cambia dispositivo LoRaWAN</div>
                      <div className="text-[10px] text-violet-800/80 mb-2">{lorawanQuery ? `Filtro attivo: "${lorawanQuery}"` : "Puoi cercare per MAC, DevEUI o nome dispositivo."}</div>
                      <select className="w-full text-xs border border-violet-200 rounded-xl px-2 py-2 bg-white" defaultValue="" onChange={(e) => { const selected = lorawanDisponibili.find((item) => item.mac_address === e.target.value); if (selected) assegnaLorawanAFrigo(selected, frigo.id); }}>
                        <option value="">{currentLorawan ? `Attuale: ${currentLorawan.mac_address}` : "Seleziona un dispositivo LoRaWAN..."}</option>
                        {lorawanDisponibili.map((item) => <option key={item.mac_address} value={item.mac_address}>{item.mac_address}{item.external_device_id ? ` - ${item.external_device_id}` : ""}{item.nome ? ` - ${item.nome}` : ""}{Number(item.frigo_id || 0) === Number(frigo.id) ? " (attuale)" : ""}</option>)}
                      </select>
                      {!lorawanDisponibili.length ? <div className="mt-2 text-[11px] text-violet-800">Nessun LoRaWAN trovato con il filtro corrente.</div> : null}
                    </div>
                  </div>
                );
              })}

              {!frighiPerAzienda.length && selectedAzienda ? <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">Nessun frigo trovato. Se hai applicato filtri, prova ad allargarli; altrimenti usa il pulsante &quot;Nuovo frigo per questa azienda&quot;.</div> : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
